import { useState, useCallback } from 'react';
import { useConfirmationModal, useHover } from '../../../../hooks';
import { dollarTemplate } from '../../../../lib/utils';
import { AlbumPhotoModal } from '../../../ProductSelection/Albums';
import styles from './AlbumOrderSummary.module.css';
import { ReactComponent as EditIcon } from '../../../../images/icons/edit.svg';
import { Link } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete.svg';
import { ReactComponent as DownloadIcon } from '../../../../images/icons/dowload-alternative.svg';
import downloadingGIF from '../../../../animations/downloading.gif';
import { useLabOrderDetailsStore } from '../../../../stores';

const fileNameFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const path = new URL(url).pathname;
  const name = path.split('/').filter(Boolean).pop();
  return name || null;
};

const ClamshellDetails = ({ clamshell }) => {

  const guid = clamshell?.guid ?? '';
  const font = clamshell?.font ?? 'None';
  const stamping = clamshell?.stamping ?? 'None';
  const outerFabric = clamshell?.outerFabricName ?? 'None';
  const trayFabric = clamshell?.trayFabricName ?? 'None';
  const preDesigned = clamshell?.preDesignedCover ?? 'None';
  const coverText = clamshell?.coverText ?? 'None';

  return (
    <div className={styles.clamshellBlock}>
      <h4 className={styles.clamshellTitle}>Clamshell Details</h4>
      {guid && (
        <p className={styles.clamshellMeta}>Clamshell GUID: {guid}</p>
      )}

      <div className={styles.clamshellGrid}>
        <div className={styles.clamshellColumn}>
          <div className={styles.colHeader}>FABRIC</div>
          <div className={styles.columnInnerGrid}>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Outer Fabric:</div>
              <div className={styles.valueRow}>{outerFabric}</div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Tray Fabric:</div>
              <div className={styles.valueRow}>{trayFabric}</div>
            </div>
          </div>
        </div>

        <div className={styles.clamshellColumn}>
          <div className={styles.colHeader}>DESIGN</div>
          <div className={styles.columnInnerGrid}>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Font:</div>
              <div className={styles.valueRow}>
                {font}
              </div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Stamping:</div>
              <div className={styles.valueRow}>
                {stamping}
              </div>
            </div>
            <div className={`${styles.columnFieldCell} ${styles.columnFieldCellFull}`}>
              <div className={styles.labelRow}>Pre-Designed Cover:</div>
              <div className={styles.valueRow}>{preDesigned}</div>
            </div>
          </div>
        </div>

        <div className={styles.clamshellColumn}>
          <div className={styles.colHeader}>COVER TEXT PROPERTIES</div>
          <div className={styles.columnInnerGrid}>
            <div className={`${styles.columnFieldCell} ${styles.columnFieldCellFull}`}>
              <div className={styles.labelRow}>Cover Text:</div>
              <div className={styles.coverTextValue}>{coverText}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CoverTextGrid = ({ albumCoverText, upperTextValue, lowerTextValue }) => {
  return (
    <div className={`${styles.columnInnerGrid} ${styles.coverTextFields}`}>
      <div className={styles.columnFieldCell}>
        <div className={styles.labelRow}>Cover text:</div>
        <div className={styles.coverTextValue}>{albumCoverText}</div>
      </div>
      <div className={styles.columnFieldCell}>
        <div className={styles.labelRow}>Upper Text Value:</div>
        <div className={styles.coverTextValue}>
          {upperTextValue}
        </div>
      </div>
      <div className={`${styles.columnFieldCell} ${styles.columnFieldCellFull}`}>
        <div className={styles.labelRow}>Lower Text Value:</div>
        <div className={styles.coverTextValue}>
          {lowerTextValue}
        </div>
      </div>
    </div>
  );
};

const AlbumOrderSummaryCard = ({ resource, showActions = false, onDeleteAlbum }) => {

  const albumOrderDetail = resource;
  if (!albumOrderDetail) return null;

  const attrs = albumOrderDetail.attributes || {};
  const labOrderId = attrs?.apiLabOrderId;
  const albumDetailId = attrs.guid;
  const albumPhotos = attrs?.albumPhotos ?? [];
  const photoCount = 1 + albumPhotos.length;
  const font = attrs?.font ?? 'None';
  const stamping = attrs?.stamping ?? 'None';
  const albumCoverText = attrs?.coverTextProperties?.coverTextValue  ?? 'None';
  const upperTextValue = attrs?.upperTextProperties?.upperTextValue ?? 'None';
  const lowerTextValue = attrs?.lowerTextProperties?.lowerTextValue ?? 'None';
  const spreadQty = attrs?.spreadQuantity ?? 10;
  const totalPrice = attrs?.totalPrice ?? '0.00';
  const outerFabric = attrs?.outerFabricName ?? 'None';
  const innerFabric = attrs?.innerFabricName ?? 'None';
  const preDesignedCover = attrs?.preDesignedCover ?? 'None';

  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [modalFileName, setModalFileName] = useState(null);
  const [modalCoverText, setModalCoverText] = useState(undefined);
  const [isDownloading, setIsDownloading] = useState(false);
  const { Modal, setIsOpen } = useConfirmationModal();
  const [hoverRef, isHovered] = useHover();
  const isAdmin = useLabOrderDetailsStore((state) => state.isAdmin);
  const downloadAlbumZip = useLabOrderDetailsStore((state) => state.downloadAlbumZip);

  const downloadAlbum = async () => {
    try {
      setIsDownloading(true);
      await downloadAlbumZip(albumDetailId);
    } finally {
      setIsDownloading(false);
    }
  };

  const openDeleteModal = useCallback(
    (e) => {
      e.preventDefault();
      setIsOpen(true);
    },
    [setIsOpen],
  );

  const closePhotoModal = useCallback(() => {
    setPhotoModalOpen(false);
    setModalImageUrl(null);
    setModalFileName(null);
    setModalCoverText(undefined);
  }, []);

  const openPhotoModal = useCallback((imageUrl, fileName, albumCoverText) => {
    setModalImageUrl(imageUrl || null);
    setModalFileName(fileName || null);
    setModalCoverText(
      albumCoverText !== undefined ? albumCoverText : undefined,
    );
    setPhotoModalOpen(true);
  }, []);

  return (
    <article className={styles.card} ref={hoverRef}>
      {isAdmin && (
        <button
          type="button"
          className={styles.downloadAlbumButton}
          aria-label="Download album ZIP"
          onClick={downloadAlbum}
          disabled={isDownloading}
        >
          <div className="link-block-14 w-inline-block">
            <div className={!isDownloading ? "icon w-embed" : ""}>
              {isDownloading ? (
                <img src={downloadingGIF} alt="Downloading..." width="40" height="40" />
              ) : (
                <DownloadIcon />
              )}
            </div>
          </div>
        </button>
      )}
      <div className={styles.cardTitleBlock}>
        <h3 className={styles.headerTitle}>{attrs.description || 'Album'}</h3>
        {showActions ? (
          <div className={styles.albumIdRow}>
            <p className={styles.meta}>Album ID: {albumDetailId}</p>
            <div className={styles.albumHeaderActions}>
              <button
                type="button"
                className={styles.deleteAlbumButton}
                aria-label="Delete"
                onClick={openDeleteModal}
                style={{
                  opacity: isHovered ? 1 : 0,
                  pointerEvents: isHovered ? 'auto' : 'none',
                }}
              >
                <DeleteIcon />
              </button>
              <Link
                to={`/product-selection/${labOrderId}/edit/line-item/${attrs.guid}`}
                className={styles.editAlbumButton}
              >
                <div className="icon smaller right-margin w-embed">
                  <EditIcon />
                </div>
                Edit
              </Link>
            </div>
          </div>
        ) : (
          <p className={styles.meta}>Album ID: {albumDetailId}</p>
        )}
      </div>

      <div className={styles.columnGrid}>
        <div className={styles.column}>
          <div className={styles.colHeader}>Description </div>
          <p className={styles.descriptionBodyText}>{attrs.description || 'None'}</p>
        </div>

        <div className={styles.column}>
          <div className={styles.colHeader}>Notes</div>
          <p className={styles.descriptionBodyText}>{attrs.notes || 'None'}</p>
        </div>

        <div className={styles.column}>
          <div className={styles.colHeader}>Fabric</div>
          <div className={styles.columnInnerGrid}>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Outer Fabric:</div>
              <div className={styles.valueRow}>{outerFabric || 'None'}</div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Inner Fabric:</div>
              <div className={styles.valueRow}>{innerFabric || 'None'}</div>
            </div>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.colHeader}>Design</div>
          <div className={styles.columnInnerGrid}>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Font:</div>
              <div className={styles.valueRow}>{font}</div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Spread Qty:</div>
              <div className={styles.valueRow}>
                {spreadQty != null ? spreadQty : 'None'}
              </div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Stamping:</div>
              <div className={styles.valueRow}>{stamping}</div>
            </div>
            <div className={styles.columnFieldCell}>
              <div className={styles.labelRow}>Pre-Designed Cover:</div>
              <div className={styles.valueRow}>{preDesignedCover || 'None'}</div>
            </div>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.colHeader}>Cover Text Properties</div>
          <CoverTextGrid albumCoverText={albumCoverText} upperTextValue={upperTextValue} lowerTextValue={lowerTextValue} />
        </div>

        <div className={styles.column}>
          <div className={styles.colHeader}>Price</div>
          <div className={styles.columnInnerGrid}>
            <div className={styles.columnFieldCell}>
              <div className={styles.price}>
                {totalPrice != null && totalPrice !== ''
                  ? dollarTemplate(totalPrice)
                  : 'None'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {attrs.clamshell !== null && (
        <ClamshellDetails clamshell={attrs.clamshell} />
      )}

      <div className={styles.photosSection}>
        <div className={styles.photosHeading}>Album photos ({photoCount})</div>
        <div className={styles.photoStrip}>
          <div className={styles.photosGrid}>
            <div
              className={styles.photoCard}
              role="button"
              tabIndex={0}
              aria-label="View cover preview enlarged"
              onClick={() => openPhotoModal(null, null, albumCoverText)}
            >
              <div className={styles.albumCoverFace}>
                <span className={styles.albumCoverFaceText}>{albumCoverText}</span>
              </div>
              <div className={styles.photoLabel}>Cover</div>
            </div>
            {albumPhotos.map((photo, idx) => {
              const pageLabel = `Page ${idx + 2}`;
              const url = photo?.photoUrl ?? null;
              const fileName = fileNameFromUrl(url);
              return (
                <div
                  key={photo?.photoGuid ?? idx}
                  className={styles.photoCard}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${pageLabel} enlarged`}
                  onClick={() => openPhotoModal(url, fileName)}
                >
                  <img
                    src={url}
                    alt={pageLabel}
                    className={styles.photoThumb}
                    loading="lazy"
                  />
                  <div className={styles.photoLabel}>{pageLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AlbumPhotoModal
        isOpen={photoModalOpen}
        onClose={closePhotoModal}
        imageUrl={modalImageUrl}
        fileName={modalFileName}
        coverText={modalCoverText}
        allowCoverTextWithoutImage
      />

      {showActions && (
        <Modal
          onContinue={() => {
            setIsOpen(false);
            if (!albumDetailId) return;
            if (typeof onDeleteAlbum === 'function') {
              onDeleteAlbum(albumDetailId);
              return;
            }
          }}
        />
      )}
    </article>
  );
};

export default AlbumOrderSummaryCard;
